import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { Card, CardBody, Col, Container, CardHeader, Row } from 'reactstrap';
// import { useNavigate } from 'react-router-dom';

import BreadCrumb from 'Components/Common/BreadCrumb';
import DeleteModal from 'Components/Common/DeleteModal';
// import { isEmpty } from 'lodash';

// Export Modal
// import ExportCSVModal from 'Components/Common/ExportCSVModal';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';

import { useSelector } from 'react-redux';
import { RootState } from 'slices';
import { createSortableHeader } from 'Components/pos2u/CreateSortableHeader';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import ModalBackdropNotFound from 'Components/pos2u/ModalBackdropNotFound';
import {
  deleteBulkCategory,
  getCategories,
  postAddCategory,
  putUpdateCategory,
} from 'services/categoryService';
import {
  CategoryFromAPI,
  CategorySearchFilters,
} from 'types/category/categoryTypes';
import CategoryTable from 'pages/Category/CategoryTable';
import {
  setPageFilters,
  // clearPageFilters,
  setEntriesPerPage,
  setCurrentPage,
  setInputPageValue,
} from 'slices/category/reducer';
import { useDispatch } from 'react-redux';
import { truncateText } from 'utils/common/String';
import MainAddButton from 'Components/pos2u/Buttons/MainAddButton';
import IconActionButton from 'Components/pos2u/Buttons/IconActionButton';
import { FaRegPenToSquare } from 'react-icons/fa6';
import { CategoryActionButtons, CategoryIconSize } from './CategoryConstants';
import { getSearchProductsCategory } from 'services/productService';
import { convertToProductCategoryIdOptions } from 'utils/products/converter';
import {
  setProductsCategoryOptions,
  setRawCategories,
} from 'slices/product/select/reducer';
import MainDeleteButton from 'Components/pos2u/Buttons/MainDeleteButton';

const CategoryPage = () => {
  const { t } = useTranslation();
  // const history = useNavigate();
  const dispatch = useDispatch();

  const layoutState = useSelector((state: RootState) => state.Layout);
  const { layoutModeType } = layoutState;
  const isDarkMode = layoutModeType === 'dark';

  const infoState = useSelector((state: RootState) => state.Info);
  const { merchant, isMerchantLoaded } = infoState;

  const CategoryState = useSelector((state: RootState) => state.Category);
  const { PageDetails, PageFilters } = CategoryState;

  const columnHelper = createColumnHelper<CategoryFromAPI>();

  const [isNotFound, setIsNotFound] = useState(false);
  const [categoriesList, setCategoriesList] = useState<CategoryFromAPI[]>([]);
  const [categoriesTotal, setCategoriesTotal] = useState<number>(0);
  const [isLoadSuccess, setIsLoadSuccess] = useState<boolean>(false);
  const [isAddingCategory, setIsAddingCategory] = useState<boolean>(false);
  const [newCategory, setNewCategory] = useState({
    name: '',
    sort_order: 0,
  });

  const inputCreateCategoryName = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isAddingCategory) {
      inputCreateCategoryName.current?.focus();
    }
  }, [isAddingCategory]);

  const fetchAllCategories = useCallback(async () => {
    if (!merchant?.id) return;
    setIsLoadSuccess(false);
    try {
      const response = await getCategories(
        `[${merchant.id}]`,
        PageFilters.categoryName,
        PageDetails.currentPage + 1,
        PageDetails.entriesPerPage
      );

      if (response) {
        const { message, error_code, data } = response;
        if (message === 'OK' && error_code === 0) {
          const { categories, total } = data;
          setCategoriesTotal(total);
          setCategoriesList(categories);

          setIsNotFound(total === 0);
        } else {
          setCategoriesTotal(0);
          setCategoriesList([]);
        }
        setIsLoadSuccess(true);
      } else {
        console.error('No response data returned');
        setCategoriesTotal(0);
        setCategoriesList([]);
        setIsLoadSuccess(true);
      }
    } catch (error) {
      console.error('Failed to fetch category data:', error);
      setCategoriesTotal(0);
      setCategoriesList([]);
      setIsLoadSuccess(true);
    }
  }, [
    PageDetails.currentPage,
    PageDetails.entriesPerPage,
    PageFilters,
    merchant,
  ]);

  useEffect(() => {
    if (isMerchantLoaded && merchant?.id) {
      fetchAllCategories();
    }
  }, [fetchAllCategories, isMerchantLoaded, merchant]);

  useEffect(() => {
    if (categoriesList) {
      setIsLoadSuccess(true);
    } else {
      setIsLoadSuccess(false);
    }
  }, [categoriesList]);

  const fetchProductCategories = useCallback(async () => {
    if (!merchant?.id) return;
    try {
      const response = await getSearchProductsCategory(`[${merchant.id}]`);

      if (response) {
        const { message, error_code, data } = response;

        if (message === 'OK' && error_code === 0) {
          const categoryOptions = data.categories
            ? await convertToProductCategoryIdOptions(data.categories, t, true)
            : [{ options: [] }];
          dispatch(setProductsCategoryOptions(categoryOptions));
          dispatch(setRawCategories(data.categories || []));
        } else {
        }
      } else {
      }
    } catch (error) {
      console.error('Failed to fetch product categories:', error);
    }
  }, [dispatch, merchant, t]);

  const [deleteModalMulti, setDeleteModalMulti] = useState<boolean>(false);

  const [selectedCheckBoxDelete, setSelectedCheckBoxDelete] = useState<
    number[]
  >([]);
  const [isMultiDeleteButton, setIsMultiDeleteButton] =
    useState<boolean>(false);
  const [isCheckAll, setIsCheckAll] = useState<boolean>(false);

  const deleteMultiple = async () => {
    const checkall = document.getElementById('checkBoxAll') as HTMLInputElement;

    if (selectedCheckBoxDelete && selectedCheckBoxDelete.length > 0) {
      const response: any = await deleteBulkCategory(selectedCheckBoxDelete);
      const { message, error_code, data } = response;
      if (message === 'OK' && error_code === 0) {
        toast.success(data.message, { autoClose: 3000 });
        fetchAllCategories();
        fetchProductCategories();
      } else {
        toast.error(message, { autoClose: 3000 });
      }
    }
    setSelectedCheckBoxDelete([]);
    setIsMultiDeleteButton(false);
    setIsCheckAll(false);
    if (checkall) {
      checkall.checked = false;
    }
  };

  const checkedAll = useCallback(() => {
    const updatedCheckAllState = !isCheckAll;
    setIsCheckAll(updatedCheckAllState);
    const ele =
      document.querySelectorAll<HTMLInputElement>('.categoryCheckBox');

    const tmp: number[] = [];
    if (updatedCheckAllState) {
      ele.forEach((ele) => {
        ele.checked = true;
        tmp.push(parseInt(ele.value, 10));
      });
    } else {
      ele.forEach((ele) => {
        ele.checked = false;
      });
    }
    const CheckedEle = document.querySelectorAll('.categoryCheckBox:checked');
    if (CheckedEle.length > 0) {
      setIsMultiDeleteButton(true);
    } else {
      setIsMultiDeleteButton(false);
    }

    setSelectedCheckBoxDelete(tmp);
  }, [isCheckAll]);

  const handleCheckboxChange = (id: number) => {
    const ele = document.querySelectorAll('.categoryCheckBox:checked');
    if (ele.length > 0) {
      setIsMultiDeleteButton(true);
    } else {
      setIsMultiDeleteButton(false);
    }

    setSelectedCheckBoxDelete((prevSelected: number[]) => {
      if (prevSelected.includes(id)) {
        return prevSelected.filter((selectedId: number) => selectedId !== id);
      } else {
        return [...prevSelected, id];
      }
    });
  };

  const toggleDisplay = useCallback(
    (selector: string, displayValue: string) => {
      const element = document.querySelector(selector);
      if (element) (element as HTMLElement).style.display = displayValue;
    },
    []
  );
  const changeInputValue = useCallback((selector: string, value: string) => {
    const element = document.querySelector(selector);
    if (element) (element as HTMLInputElement).value = value;
  }, []);

  const changeDivValue = useCallback((selector: string, value: string) => {
    const element = document.querySelector(selector);
    if (element) (element as HTMLDivElement).innerHTML = value;
  }, []);

  const changeRowColor = useCallback((selector: string, color: string) => {
    const rowElement = document.querySelector(selector);
    if (rowElement) {
      (rowElement as HTMLTableRowElement).style.backgroundColor = color;
    }
  }, []);

  const handleCancelClick = useCallback(
    (id: number, name: string, sort_order: number) => {
      toggleDisplay(`.cn-${id}`, 'block');
      toggleDisplay(`.sort-${id}`, 'block');
      toggleDisplay(`#edit-btn-${id}`, 'flex');

      toggleDisplay(`.cn-input-${id}`, 'none');
      toggleDisplay(`.sort-input-${id}`, 'none');
      toggleDisplay(`#save-btn-${id}`, 'none');
      toggleDisplay(`#cancel-btn-${id}`, 'none');

      changeInputValue(`#categoryName-${id}`, name);
      changeInputValue(`#sortOrder-${id}`, sort_order.toString());
    },
    [toggleDisplay, changeInputValue]
  );

  const handleEditClick = useCallback(
    (id: number) => {
      toggleDisplay(`.cn-${id}`, 'none');
      toggleDisplay(`.sort-${id}`, 'none');
      toggleDisplay(`#edit-btn-${id}`, 'none');

      toggleDisplay(`.cn-input-${id}`, 'block');
      toggleDisplay(`.sort-input-${id}`, 'block');
      toggleDisplay(`#save-btn-${id}`, 'block');
      toggleDisplay(`#cancel-btn-${id}`, 'block');

      const categoryName = document.getElementById(
        `categoryName-${id}`
      ) as HTMLInputElement;
      categoryName.focus();
    },
    [toggleDisplay]
  );

  const handleSaveSubmit = useCallback(
    async (id: number) => {
      toggleDisplay(`.cn-${id}`, 'block');
      toggleDisplay(`.sort-${id}`, 'block');
      toggleDisplay(`#edit-btn-${id}`, 'flex');

      toggleDisplay(`.cn-input-${id}`, 'none');
      toggleDisplay(`.sort-input-${id}`, 'none');
      toggleDisplay(`#save-btn-${id}`, 'none');
      toggleDisplay(`#cancel-btn-${id}`, 'none');

      const categoryName = document.getElementById(
        `categoryName-${id}`
      ) as HTMLInputElement;
      const sortOrder = document.getElementById(
        `sortOrder-${id}`
      ) as HTMLInputElement;

      const categoryData = {
        category_number: id,
        name: categoryName.value,
        sort_order: parseInt(sortOrder.value, 10),
      };

      try {
        const response = await putUpdateCategory(categoryData);
        if (response) {
          const { message, error_code } = response;
          if (message === 'OK' && error_code === 0) {
            changeDivValue(`.cn-${id}`, categoryName.value);
            changeDivValue(
              `.sort-${id}`,
              parseInt(sortOrder.value, 10).toString()
            );
            changeRowColor(`.row-${id}`, '#fef4e4');

            toast.success('Category Updated Successfully', { autoClose: 3000 });
          } else {
            toast.error(message, { autoClose: 3000 });
          }
        }
      } catch (error) {
        console.error('Failed to update category:', error);
        toast.error('An error occurred while updating the category.', {
          autoClose: 3000,
        });
      }
    },
    [toggleDisplay, changeDivValue, changeRowColor]
  );

  const handleCreateCategory = async () => {
    const categoryName = document.getElementById(
      'create-categoryName'
    ) as HTMLInputElement;
    const sortOrder = document.getElementById(
      'create-categorySortOrder'
    ) as HTMLInputElement;
    if (categoryName.value === '') {
      categoryName.focus();
      return false;
    }
    const categoryData = {
      name: categoryName.value,
      sort_order: parseInt(sortOrder.value, 10),
    };

    try {
      const response = await postAddCategory(categoryData);
      if (response) {
        const { message, error_code } = response;
        if (message === 'OK' && error_code === 0) {
          setIsAddingCategory(false);
          setNewCategory({ name: '', sort_order: 0 });
          toast.success('Category Created Successfully', { autoClose: 3000 });
          fetchProductCategories();
          fetchAllCategories();
        } else {
          toast.error(message, { autoClose: 3000 });
        }
      }
    } catch (error) {
      console.error('Failed to create category:', error);
      toast.error('An error occurred while creating the category.', {
        autoClose: 3000,
      });
    }
  };
  const handleCancelCreateCategory = useCallback(() => {
    setIsAddingCategory(false);
    setNewCategory({ name: '', sort_order: 0 });
  }, []);

  // Column
  const CategoryColumns: ColumnDef<CategoryFromAPI, any>[] = useMemo(
    () => [
      columnHelper.display({
        id: '#',
        header: () => (
          <input
            type="checkbox"
            id="checkBoxAll"
            data-testid="checkbox-all"
            className="form-check-input"
            onChange={() => checkedAll()}
            checked={isCheckAll}
          />
        ),
        cell: (cell) => (
          <input
            type="checkbox"
            className="categoryCheckBox form-check-input"
            value={cell.row.original.category_number}
            data-testid={`checkbox-promo-${cell.row.original.category_number}`}
            // onChange={() => deleteCheckbox()}
            checked={selectedCheckBoxDelete.includes(
              cell.row.original.category_number
            )}
            onChange={() =>
              handleCheckboxChange(cell.row.original.category_number)
            }
          />
        ),
        enableColumnFilter: false,
        enableSorting: false,
      }),
      columnHelper.accessor('name', {
        header: ({ column }) =>
          createSortableHeader(t('Category Name'), column),
        meta: {
          style: {
            // alignContent: 'flex-start',
            // minWidth: '20%',
            // maxWidth: '40%',
            // whiteSpace: 'nowrap',
            // overflow: 'hidden',
            // textOverflow: 'ellipsis',
            wordBreak: 'break-word',
            whiteSpace: 'normal',
          },
        },
        cell: (cell) => {
          const categoryName = cell.getValue();
          const ID = cell.row.original.category_number;
          return (
            <div>
              <div className={`cn-${ID}`} title="">
                {truncateText(categoryName, 50)}
              </div>
              <div className={`cn-input-${ID}`} style={{ display: 'none' }}>
                <input
                  id={`categoryName-${ID}`}
                  style={{ width: '80%' }}
                  type="text"
                  defaultValue={categoryName}
                  className="form-control"
                />
              </div>
            </div>
          );
        },
        enableColumnFilter: false,
      }),
      columnHelper.accessor('sort_order', {
        header: ({ column }) => createSortableHeader(t('Order'), column),
        cell: (cell) => {
          const sortOrder = cell.getValue();
          const ID = cell.row.original.category_number;
          return (
            <div>
              <div className={`sort-${ID}`}>{sortOrder}</div>
              <div className={`sort-input-${ID}`} style={{ display: 'none' }}>
                <input
                  id={`sortOrder-${ID}`}
                  style={{ width: '80%' }}
                  type="number"
                  defaultValue={sortOrder}
                  className="form-control"
                  onFocus={(e) => e.target.select()}
                />
              </div>
            </div>
          );
        },
        enableColumnFilter: false,
        sortingFn: 'alphanumeric',
      }),

      columnHelper.display({
        id: 'action',
        header: 'Action',
        cell: (cell) => (
          <ul className="list-inline hstack gap-2 mb-0">
            <li className="list-inline-item">
              <div className="d-flex gap-2">
                {/* <button
                  type="button"
                  className="btn btn-info btn-xl"
                  id={`edit-btn-${cell.row.original.category_number}`}
                  onClick={() =>
                    handleEditClick(cell.row.original.category_number)
                  }
                >
                  {t('Edit')}
                </button> */}

                <IconActionButton
                  id={`edit-btn-${cell.row.original.category_number}`}
                  onClick={() =>
                    handleEditClick(cell.row.original.category_number)
                  }
                  icon={
                    <FaRegPenToSquare fontSize={CategoryActionButtons.icon} />
                  }
                  title={t('Edit')}
                  height={CategoryActionButtons.button.height}
                  width={CategoryActionButtons.button.width}
                />
                <button
                  type="button"
                  // className="btn btn-secondary btn-xl"
                  className="btn w-sm btn-primary material-shadow-none"
                  style={{ display: 'none' }}
                  id={`save-btn-${cell.row.original.category_number}`}
                  onClick={() =>
                    handleSaveSubmit(cell.row.original.category_number)
                  }
                >
                  {t('Btn_Save')}
                </button>
                <button
                  type="button"
                  className="btn w-sm btn-light material-shadow-none"
                  style={{ display: 'none' }}
                  id={`cancel-btn-${cell.row.original.category_number}`}
                  onClick={() =>
                    handleCancelClick(
                      cell.row.original.category_number,
                      cell.row.original.name,
                      cell.row.original.sort_order
                    )
                  }
                >
                  {t('Btn_Cancel')}
                </button>
              </div>
            </li>
          </ul>
        ),
        enableColumnFilter: false,
        enableSorting: false,
      }),
    ],
    [
      checkedAll,
      isCheckAll,
      t,
      columnHelper,
      selectedCheckBoxDelete,
      handleSaveSubmit,
      handleCancelClick,
      handleEditClick,
    ]
  );

  // Export Modal
  // const [isExportCSV, setIsExportCSV] = useState<boolean>(false);

  document.title = t('Categories List') + ' | POS2U';

  const totalPages = Math.ceil(categoriesTotal / PageDetails.entriesPerPage);

  const handlePageChange = (newPage: number) => {
    dispatch(setCurrentPage(newPage));
  };
  const handleFilterSearchSubmit = (searchFilters: CategorySearchFilters) => {
    setIsCheckAll(false);
    setIsMultiDeleteButton(false);
    setSelectedCheckBoxDelete([]);
    dispatch(
      setPageFilters((prev: CategorySearchFilters) => ({
        ...prev,
        ...searchFilters,
      }))
    );
    dispatch(setCurrentPage(0));
    // setIsLoadSuccess(false);
  };

  return (
    <div className="page-content">
      {/* <ExportCSVModal
        show={isExportCSV}
        onCloseClick={() => setIsExportCSV(false)}
        data={orders}
      />
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteOrder}
        onCloseClick={() => setDeleteModal(false)}
      /> */}
      <ModalBackdropNotFound
        isNotFound={isNotFound}
        setIsNotFound={setIsNotFound}
        message={`${t('Not found for Promotions')}`}
        ButtonMsg="OK"
      />
      <DeleteModal
        show={deleteModalMulti}
        onDeleteClick={() => {
          deleteMultiple();
          setDeleteModalMulti(false);
        }}
        onCloseClick={() => setDeleteModalMulti(false)}
        title={t('Delete Category')}
        message={
          selectedCheckBoxDelete.length === 1
            ? t('SingleDeleteAlertMessage')
            : t('MultiDeleteAlertMessage', {
                items: selectedCheckBoxDelete.length,
              })
        }
        cancelBtnMsg={t('Cancel')}
        confirmBtnMsg={t('Delete')}
      />
      <Container fluid>
        <BreadCrumb
          title={t('Categories List')}
          pageTitle={t('Product Categories')}
        />
        <Row>
          <Col lg={12}>
            <Card id="orderList">
              <CardHeader className="card-header border-0">
                <Row className="align-items-center gy-3">
                  <div className="col-sm">
                    <h5 className="card-title mb-0">{t('Categories List')}</h5>
                  </div>
                  <div className="col-sm-auto">
                    <div className="d-flex gap-1 flex-wrap">
                      {/* <button
                        type="button"
                        className="btn btn-success add-btn"
                        id="add-category-btn"
                        onClick={() => {
                          setIsAddingCategory(!isAddingCategory);
                        }}
                      >
                        <i className="ri-add-line align-bottom me-1"></i>
                        {t('Add Category')}
                      </button> */}
                      <MainAddButton
                        to=""
                        label={t('Add Category')}
                        id="add-category-btn"
                        onClick={() => {
                          setIsAddingCategory(!isAddingCategory);
                        }}
                        soft={true}
                      />{' '}
                      {/* <button
                        type="button"
                        className="btn btn-info"
                        onClick={() => setIsExportCSV(true)}
                      >
                        <i className="ri-file-download-line align-bottom me-1"></i>{' '}
                        Export
                      </button>{' '} */}
                      {isMultiDeleteButton && (
                        // <button
                        //   className="btn btn-soft-danger"
                        //   data-testid="multi-delete-button"
                        //   onClick={() => setDeleteModalMulti(true)}
                        // >
                        //   <RiDeleteBin2Line size={CategoryIconSize} />
                        // </button>

                        <MainDeleteButton
                          onClick={() => setDeleteModalMulti(true)}
                          iconSize={CategoryIconSize}
                        />
                      )}
                    </div>
                  </div>
                </Row>
              </CardHeader>

              <CardBody className="pt-0">
                <div>
                  <CategoryTable
                    columns={CategoryColumns}
                    data={categoriesList || []}
                    customPageSize={PageDetails.entriesPerPage}
                    divClass="table-responsive table-card mb-1 mt-0"
                    tableClass="align-middle table-nowrap"
                    theadClass={`table-${
                      isDarkMode ? 'dark' : 'light'
                    } text-muted text-uppercase`}
                    isCategoryQuery={true}
                    selectedCheckBoxDelete={selectedCheckBoxDelete.length}
                    TotalData={categoriesTotal}
                    totalPages={totalPages}
                    setEntriesPerPage={setEntriesPerPage}
                    currentPage={PageDetails.currentPage}
                    handlePageChange={handlePageChange}
                    handleSearchFiltersSubmit={handleFilterSearchSubmit}
                    initialFilters={PageFilters}
                    inputPageValue={PageDetails.inputPageValue}
                    setInputPageValue={setInputPageValue}
                    handleCreateCategory={handleCreateCategory}
                    isAddingCategory={isAddingCategory}
                    newCategory={newCategory}
                    setNewCategory={setNewCategory}
                    handleCancelCreateCategory={handleCancelCreateCategory}
                    inputRef={inputCreateCategoryName}
                    isTableLoadSuccess={isLoadSuccess}
                  />
                </div>
                <ToastContainer closeButton={false} limit={1} />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CategoryPage;
